import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Dataset {
  id: string
  name: string
  source: string
  format: string
  row_count: number
  size_gb: number
  quality_score: number
  last_updated: string
  owner: string
}

interface Pipeline {
  id: string
  name: string
  source_dataset: string
  status: string
  schedule: string
  last_run: string
  success_rate: number
}

export default function DataLakePage() {
  const [datasets, setDatasets] = useState<Dataset[]>([])
  const [pipelines, setPipelines] = useState<Pipeline[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [dataRes, pipeRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/datalake/datasets'),
      fetch(API_BASE + '/datalake/pipelines'),
      fetch(API_BASE + '/datalake/statistics')
    ])
    if (dataRes.ok) setDatasets((await dataRes.json()).datasets || [])
    if (pipeRes.ok) setPipelines((await pipeRes.json()).pipelines || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'paused': return 'bg-yellow-100 text-yellow-800'
      case 'error': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Data Lake Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Pipeline</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Datasets</div>
              <div className="text-2xl font-bold">{statistics.total_datasets}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Storage Used</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_storage_tb} TB</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Pipelines</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_pipelines}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Data Quality</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.avg_data_quality}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Daily Ingestion</div>
              <div className="text-2xl font-bold">{statistics.daily_ingestion_gb} GB</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Datasets</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quality</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {datasets.slice(0, 10).map(dataset => (
                  <tr key={dataset.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-sm">{dataset.name}</div>
                      <div className="text-xs text-gray-500">{dataset.row_count.toLocaleString()} rows</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm capitalize">{dataset.source}</div>
                      <div className="text-xs text-gray-500">{dataset.format}</div>
                    </td>
                    <td className="px-6 py-4 text-sm">{dataset.size_gb} GB</td>
                    <td className="px-6 py-4">
                      <div className={`text-sm font-medium ${dataset.quality_score >= 95 ? 'text-green-600' : dataset.quality_score >= 85 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {dataset.quality_score}%
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Pipelines</h3>
            <div className="space-y-3">
              {pipelines.slice(0, 10).map(pipeline => (
                <div key={pipeline.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm truncate flex-1">{pipeline.name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ml-2 ${getStatusColor(pipeline.status)}`}>{pipeline.status}</span>
                  </div>
                  <div className="text-xs text-gray-500">{pipeline.source_dataset}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600 capitalize">{pipeline.schedule}</span>
                    <span className="text-xs text-green-600">{pipeline.success_rate}% success</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
